import { AlertTriangle, Clock3, Play, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArtifactViewer, WorkspaceNotice, WorkspaceUnavailable } from "../components/ProductWorkspaceView";
import { PageHeading } from "../components/PageHeading";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { api } from "../lib/api";
import { handlePressedButtonGroupKey } from "../lib/buttonGroup";

export function CaseWorkbenchPage() {
  const { track } = usePortal();
  const capabilities = useCapabilities();
  const workspace = track?.product_workspace;
  const navigate = useNavigate();
  const [caseId, setCaseId] = useState("");
  const [artifactId, setArtifactId] = useState("");
  const [factBoundary, setFactBoundary] = useState<"confirmed" | "assumption" | "unknown">("unknown");
  const [selectedRisks, setSelectedRisks] = useState<string[]>([]);
  const [evidenceLevel, setEvidenceLevel] = useState<"sufficient" | "partial" | "insufficient">("insufficient");
  const [confidence, setConfidence] = useState<"low" | "medium" | "high">("low");
  const [startError, setStartError] = useState<string | null>(null);
  const selectedCase = workspace?.cases.find((item) => item.id === caseId) || workspace?.cases[0];
  const caseArtifacts = useMemo(() => selectedCase?.artifact_ids.map((id) => workspace?.artifacts.find((item) => item.id === id)).filter(Boolean) || [], [selectedCase, workspace]);
  const artifact = caseArtifacts.find((item) => item?.id === artifactId) || caseArtifacts[0];
  if (!track) return <div className="loading-state">正在读取案例工作台</div>;

  async function startCase() {
    if (!selectedCase) return;
    setStartError(null);
    try {
      const duration = Number.parseInt(selectedCase.context.timebox, 10) || 25;
      const session = await api.startSession(track!.slug, {
        duration,
        deck: selectedCase.practice_deck_id,
        preflight_judgment: {
          case_id: selectedCase.id,
          information_boundary: factBoundary,
          primary_risks: selectedRisks,
          evidence_sufficiency: evidenceLevel,
          confidence,
        },
      });
      navigate(`/learn/session/${session.id}`);
    } catch (reason) {
      setStartError(reason instanceof Error ? reason.message : String(reason));
    }
  }

  function selectCase(nextCaseId: string) {
    setCaseId(nextCaseId);
    setArtifactId("");
    setFactBoundary("unknown");
    setSelectedRisks([]);
    setEvidenceLevel("insufficient");
    setConfidence("low");
    setStartError(null);
  }

  return <>
    <PageHeading number="11" title="案例工作台" description="先读冻结上下文和产物，再写判断；启动训练时复用现有练习会话，不建立第二套答题链路。" />
    {!workspace ? <WorkspaceUnavailable /> : <>
      <WorkspaceNotice workspace={workspace} />
      {!workspace.cases.length ? <WorkspaceUnavailable reason="当前工作区没有训练案例。" /> : <>
        <div className="case-selector" role="group" aria-label="选择案例">{workspace.cases.map((item) => <button type="button" data-group-option aria-pressed={selectedCase?.id === item.id} className={selectedCase?.id === item.id ? "is-selected" : ""} key={item.id} onClick={() => selectCase(item.id)} onKeyDown={(event) => handlePressedButtonGroupKey(event, workspace.cases.map((entry) => entry.id), selectedCase?.id || "", selectCase)}>{item.title}<span>{item.split} · {item.status}</span></button>)}</div>
        {selectedCase && <div className="case-workbench-grid">
          <aside className="case-context-panel">
            <span className="context-label">案例上下文 · {selectedCase.domain}</span>
            <h2>{selectedCase.title}</h2>
            <p>{selectedCase.prompt}</p>
            <dl><div><dt>角色</dt><dd>{selectedCase.context.role}</dd></div><div><dt>任务</dt><dd>{selectedCase.context.task}</dd></div><div><dt>时限</dt><dd><Clock3 size={14} />{selectedCase.context.timebox}</dd></div><div><dt>能力</dt><dd>{selectedCase.competency_ids.join(" · ")}</dd></div></dl>
            <h3>约束</h3><ul>{selectedCase.context.constraints.map((item) => <li key={item}>{item}</li>)}</ul>
            <h3>风险提示</h3><ul className="risk-list">{selectedCase.risk_notes.map((item) => <li key={item}>{item}</li>)}</ul>
          </aside>
          <section className="case-artifact-panel">
            <div className="artifact-tabs" role="group" aria-label="案例产物">{caseArtifacts.map((item) => item && <button type="button" data-group-option aria-pressed={artifact?.id === item.id} className={artifact?.id === item.id ? "is-selected" : ""} onClick={() => setArtifactId(item.id)} onKeyDown={(event) => handlePressedButtonGroupKey(event, caseArtifacts.flatMap((entry) => entry ? [entry.id] : []), artifact?.id || "", setArtifactId)} key={item.id}>{item.title}</button>)}</div>
            <ArtifactViewer artifact={artifact} />
          </section>
          <aside className="judgment-panel">
            <span className="context-label">训练前判断</span>
            <h2>结构化预判</h2>
            <fieldset className="judgment-fieldset"><legend>当前信息边界</legend><div className="segmented-control"><button type="button" className={factBoundary === "confirmed" ? "is-selected" : ""} onClick={() => setFactBoundary("confirmed")}>已确认</button><button type="button" className={factBoundary === "assumption" ? "is-selected" : ""} onClick={() => setFactBoundary("assumption")}>含假设</button><button type="button" className={factBoundary === "unknown" ? "is-selected" : ""} onClick={() => setFactBoundary("unknown")}>关键未知</button></div></fieldset>
            <fieldset className="judgment-fieldset"><legend>主要风险</legend><div className="risk-checkboxes">{selectedCase.risk_notes.map((risk) => <label key={risk}><input type="checkbox" checked={selectedRisks.includes(risk)} onChange={() => setSelectedRisks((items) => items.includes(risk) ? items.filter((item) => item !== risk) : [...items, risk])} />{risk}</label>)}</div></fieldset>
            <fieldset className="judgment-fieldset"><legend>证据充分性</legend><div className="segmented-control"><button type="button" className={evidenceLevel === "sufficient" ? "is-selected" : ""} onClick={() => setEvidenceLevel("sufficient")}>充分</button><button type="button" className={evidenceLevel === "partial" ? "is-selected" : ""} onClick={() => setEvidenceLevel("partial")}>部分</button><button type="button" className={evidenceLevel === "insufficient" ? "is-selected" : ""} onClick={() => setEvidenceLevel("insufficient")}>不足</button></div></fieldset>
            <fieldset className="judgment-fieldset"><legend>判断信心</legend><div className="segmented-control"><button type="button" className={confidence === "low" ? "is-selected" : ""} onClick={() => setConfidence("low")}>低</button><button type="button" className={confidence === "medium" ? "is-selected" : ""} onClick={() => setConfidence("medium")}>中</button><button type="button" className={confidence === "high" ? "is-selected" : ""} onClick={() => setConfidence("high")}>高</button></div></fieldset>
            {selectedCase.original_answer && <div className="immutable-answer"><ShieldCheck size={15} /><strong>原始回答已冻结</strong><time>{selectedCase.original_answer.submitted_at}</time><p>{selectedCase.original_answer.content}</p></div>}
            {startError && <p className="form-error" role="alert"><AlertTriangle size={15} />{startError}</p>}
            <button className="command-button" type="button" disabled={!capabilities.practice.writable || !selectedRisks.length} onClick={() => void startCase()}><Play size={16} />启动训练案例</button>
            {!selectedRisks.length && <p className="method-note">选择至少一个主要风险后启动；正式回答在练习会话中完成。</p>}
            {!capabilities.practice.writable && <p className="method-note">当前为只读模式，浏览可用，但服务端会拒绝创建练习会话。</p>}
          </aside>
        </div>}
      </>}
    </>}
  </>;
}
